import { Variables } from '@/services/backstage/config';
import { getUniqueIdentifier, useGraph, useGraphMutation, useResource } from '@/services/sidecar/useGraphProvider';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import { useVariable } from '@softwareimaging/backstage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInvalidateList } from './explorer';

export const useDriveItemResource = (itemId: string) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const { client } = useGraph();
  return client
    .api<DriveItem & { '@microsoft.graph.downloadUrl': string }>(`/drives/${driveId}/items/${itemId}`)
    .expand(['thumbnails', 'listItem'])
    .select([
      'id',
      'name',
      'thumbnails',
      'folder',
      'createdDateTime',
      'image',
      'video',
      'file',
      'content.downloadUrl',
      'parentReference',
      'listItem'
    ]);
};

export const useDriveItem = (itemId: string) => {
  const resource = useDriveItemResource(itemId);
  const [driveItem, ...extra] = useResource(() => resource);
  const id = driveItem.id;
  if (!id) throw new Error(`List item "${id}" does not have an id.`);

  return [
    {
      ...driveItem
    },
    extra
  ] as const;
};

export const useDriveItemsResource = (itemIds: string[]) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const { client } = useGraph();

  const resources = itemIds.map(itemId =>
    client
      .api<DriveItem & { '@microsoft.graph.downloadUrl': string }>(`/drives/${driveId}/items/${itemId}`)
      .expand(['thumbnails'])
      .select([
        'id',
        'name',
        'thumbnails',
        'folder',
        'createdDateTime',
        'image',
        'video',
        'file',
        'content.downloadUrl'
      ])
  );

  return resources;
};

export const useDownloadDriveItems = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('graphDriveId');
  if (!driveId) throw new Error('Missing driveId');
  return useMutation(async (args: { ids: string[] }) => {
    const requests = args.ids.map(async (id: string) => {
      const item = await client
        .api<DriveItem & { '@microsoft.graph.downloadUrl': string }>(`/drives/${driveId}/items/${id}`)
        .get();

      const downloadUrl = item['@microsoft.graph.downloadUrl'];
      if (!downloadUrl) {
        throw new Error('Download URL is undefined');
      }
      const response = await fetch(downloadUrl);
      const data = await response.blob();
      const url = window.URL.createObjectURL(data);
      return [url, item] as const;
    });
    return Promise.all(requests);
  });
};

export const useDeleteItems = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('graphDriveId');

  return useMutation(async (ids: string[]) => {
    const requests = ids.map(async (id: string) => {
      return await client.api(`/drives/${driveId}/items/${id}`).delete();
    });
    return Promise.all(requests);
  });
};

export const useInvalidateDriveItem = (itemId: string) => {
  const item = useDriveItemResource(itemId);
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries([getUniqueIdentifier(item)]);
};

const useChildrenResource = (folderId: string) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const { client } = useGraph();
  return client
    .api<(DriveItem & { '@microsoft.graph.downloadUrl': string })[]>(`/drives/${driveId}/items/${folderId}/children`)
    .expand(['thumbnails', 'listItem'])
    .select([
      'id',
      'name',
      'thumbnails',
      'folder',
      'createdDateTime',
      'image',
      'video',
      'content.downloadUrl',
      'file',
      'listItem'
    ]);
};

export const useChildren = (folderId: string) => {
  const resource = useChildrenResource(folderId);
  const [folder, ...extra] = useResource(() => resource);
  return [{ value: folder }, ...extra] as const;
};

export const useInvalidateChildren = (folderId: string) => {
  const children = useChildrenResource(folderId);
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries([getUniqueIdentifier(children)]);
  };
};

export const usePreview = (itemId: string) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const { client } = useGraph();
  const resource = client.api<{ getUrl: string }>(`/drives/${driveId}/items/${itemId}/preview`);
  return useQuery([getUniqueIdentifier(resource), 'preview'], () => {
    return resource.post({});
  });
};

export const useResourceThumbnail = (folderId: string) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const [response, options] = useResource(client => {
    return client
      .api<DriveItem[]>(`/drives/${driveId}/items/${folderId}/children`)
      .expand(['thumbnails'])
      .top(2)
      .select(['id', 'thumbnails', 'file']);
  });
  return [response.value.find(a => a.file?.mimeType?.startsWith('image'))?.thumbnails?.[0], options] as const;
};

export const useCreateFolder = (parentFolderId: string) => {
  const driveId = useVariable<Variables>('graphDriveId');
  const invalidateChildren = useInvalidateChildren(parentFolderId);
  const invalidateList = useInvalidateList();
  const { client } = useGraph();
  return useGraphMutation<DriveItem, { name: string }>(async ({ name }) => {
    const response = await client
      .api<DriveItem>(`/drives/${driveId}/items/${parentFolderId}/children`)
      .select(['id', 'name'])
      .post({
        name,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'rename'
      });

    await invalidateList();
    await invalidateChildren();

    return response;
  });
};

export const useDownloadItem = () => {
  const { client } = useGraph();
  const driveId = useVariable<Variables>('graphDriveId');

  return async (id: string) => {
    // @ts-ignore
    const response = await client.api(`/drives/${driveId}/items/${id}`).select('content.downloadUrl').get();
    // @ts-ignore
    const url = response['@microsoft.graph.downloadUrl'];
    if (url) {
      window.location.assign(url);
    }
  };
};

export const useMoveItem = () => {
  const { client } = useGraph();
  const queryClient = useQueryClient();
  const driveId = useVariable<Variables>('graphDriveId');
  return useGraphMutation(
    async ({ itemId, destinationId, name }: { itemId: string; destinationId: string; name?: string }) => {
      await client.api(`/drives/${driveId}/items/${itemId}`).patch({ parentReference: { id: destinationId }, name });
      const original = client
        .api<DriveItem & { '@microsoft.graph.downloadUrl': string }>(`/drives/${driveId}/items/${itemId}`)
        .expand(['thumbnails', 'listItem'])
        .select([
          'id',
          'name',
          'thumbnails',
          'folder',
          'createdDateTime',
          'image',
          'video',
          'file',
          'content.downloadUrl',
          'parentReference',
          'listItem'
        ]);
      await queryClient.invalidateQueries([getUniqueIdentifier(original)]);
    }
  );
};
